import { Link as RouterLink } from 'react-router-dom';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { formatEntityTagsLabel } from '@/features/shared/tags';
import { s } from '@/theme/tokens';
import { InfoField } from '@/ui/composites/InfoField';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { LayoutGrid, LayoutGridItem } from '@/ui/layout/LayoutGrid';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Chip, formatStatusLabel, Link, resolveStatusChipTone, Surface } from '@/ui/primitives';
import type { PipelineDetailDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

export type PipelineDetailsPanelsProps = {
  pipeline: PipelineDetailDto;
};

export const PipelineDetailsPanels = ({ pipeline }: PipelineDetailsPanelsProps): ReactElement => {
  const noDataLabel = 'No data';
  const projectLabel = pipeline.project?.name ?? pipeline.project?.slug ?? pipeline.projectId;
  const pipelineSlug = resolvePipelineSlug(pipeline);
  const pipelineHref = `/pipelines/${pipelineSlug}`;
  const latestBuild = pipeline.latestBuild ?? null;
  const latestBuildRun = resolveBuildRunNumber(latestBuild?.buildKey ?? null);
  const latestBuildHref =
    latestBuildRun !== null ? `/pipelines/${pipelineSlug}/runs/${latestBuildRun}` : null;
  const latestRunLabel = latestBuildRun !== null ? `#${latestBuildRun}` : noDataLabel;
  const latestTimestampLabel = latestBuild
    ? formatDate(latestBuild.timestamp, { includeTime: true }) || noDataLabel
    : noDataLabel;
  const latestDurationLabel =
    latestBuild?.durationMs !== null && latestBuild?.durationMs !== undefined
      ? formatDuration(latestBuild.durationMs)
      : noDataLabel;
  const latestStatusLabel = latestBuild?.status
    ? formatStatusLabel(latestBuild.status)
    : noDataLabel;
  const providerLabel = pipeline.provider.toUpperCase();
  const branchLabel = pipeline.defaultBranch ?? noDataLabel;
  const keyLabel = pipeline.pipelineKey ?? noDataLabel;
  const urlLabel = pipeline.pipelineUrl ?? noDataLabel;
  const tagsLabel = formatEntityTagsLabel(pipeline.tags);

  return (
    <Section paddingY="md">
      <Surface padding="lg" border>
        <LayoutGrid gap="md">
          <LayoutGridItem
            xs={12}
            sm={4}
            sx={{ borderRight: 0.5, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingRight: s('md'), width: '100%' }}>
              <InfoField
                label="Pipeline"
                value={
                  <Link component={RouterLink} to={pipelineHref} underline="hover" color="inherit">
                    {pipeline.name}
                  </Link>
                }
                gap="none"
              />
              <InfoField label="Project" value={projectLabel} gap="none" />
              <InfoField label="Provider" value={providerLabel} gap="none" />
              <InfoField label="Tags" value={tagsLabel} gap="none" />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem
            xs={12}
            sm={4}
            sx={{ borderRight: 0.5, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingX: s('md'), width: '100%' }}>
              <InfoField label="Pipeline Key" value={keyLabel} gap="none" />
              <InfoField label="Default Branch" value={branchLabel} gap="none" />
              <InfoField
                label="Pipeline URL"
                value={
                  pipeline.pipelineUrl ? (
                    <Link href={pipeline.pipelineUrl} target="_blank" rel="noopener noreferrer">
                      {urlLabel}
                    </Link>
                  ) : (
                    urlLabel
                  )
                }
                gap="none"
              />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem xs={12} sm={4}>
            <Stack gap="sm" sx={{ paddingLeft: s('md') }}>
              <InfoField
                label="Latest Run"
                value={
                  latestBuildHref ? (
                    <Link
                      component={RouterLink}
                      to={latestBuildHref}
                      underline="hover"
                      color="inherit"
                    >
                      {latestRunLabel}
                    </Link>
                  ) : (
                    latestRunLabel
                  )
                }
                gap="none"
              />
              <InfoField label="Latest Timestamp" value={latestTimestampLabel} gap="none" />
              <InfoField label="Latest Duration" value={latestDurationLabel} gap="none" />
              <InfoField
                label="Latest Status"
                value={
                  latestBuild?.status ? (
                    <Chip
                      label={latestStatusLabel}
                      tone={resolveStatusChipTone(latestBuild.status)}
                      size="xs"
                      transform="uppercase"
                    />
                  ) : (
                    latestStatusLabel
                  )
                }
                gap="none"
              />
            </Stack>
          </LayoutGridItem>
        </LayoutGrid>
      </Surface>
    </Section>
  );
};
